import { useState, useEffect } from "react";
import { useTheme } from "../context/ThemeContext";
import { getBalances, syncPlaid } from "../api/client";

const fmt = (n) => `$${Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const AccountCard = ({ account, COLORS }) => {
  const balance = parseFloat(account.balance || 0);
  const isCredit = account.type === "credit";
  const isSavings = account.show_as_savings || ["savings","money market","cd","hsa"].includes(account.subtype);
  const balanceColor = isCredit ? COLORS.red : COLORS.green;
  const typeLabel = isCredit ? "Credit" : isSavings ? "Savings" : account.subtype?.charAt(0).toUpperCase() + account.subtype?.slice(1) || account.type;

  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: `1px solid ${COLORS.border}` }}>
      <div>
        <div style={{ color: COLORS.text, fontSize: 14, fontWeight: 600 }}>{account.custom_name || account.name}</div>
        <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 2, textTransform: "capitalize" }}>{typeLabel}</div>
      </div>
      <div style={{ textAlign: "right" }}>
        <div style={{ color: balanceColor, fontFamily: "monospace", fontWeight: 700, fontSize: 15 }}>{isCredit ? "-" : ""}{fmt(balance)}</div>
        {account.last_synced && (
          <div style={{ color: COLORS.textMuted, fontSize: 10, marginTop: 2 }}>
            {new Date(account.last_synced).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
          </div>
        )}
      </div>
    </div>
  );
};

const SectionHeader = ({ title, total, totalColor, COLORS }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
    <h3 style={{ color: COLORS.text, fontWeight: 700, fontSize: 14, margin: 0 }}>{title}</h3>
    <span style={{ color: totalColor, fontFamily: "monospace", fontWeight: 700, fontSize: 14 }}>{fmt(total)}</span>
  </div>
);

const Accounts = () => {
  const { COLORS } = useTheme();
  const [balances, setBalances] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await getBalances();
      setBalances(r.data);
    } catch(e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSync = async () => {
    setSyncing(true);
    try { await syncPlaid(); await load(); } catch(e) {}
    finally { setSyncing(false); }
  };

  if (loading) return (
    <div style={{ padding: 24, color: COLORS.textMuted, textAlign: "center", paddingTop: 80 }}>Loading...</div>
  );

  const allAccounts = balances?.accounts || [];

  const checkingAccs   = allAccounts.filter(a => a.type === "depository" && !a.show_as_savings && ["checking","prepaid"].includes(a.subtype));
  const savingsAccs    = allAccounts.filter(a => a.type === "depository" && (a.show_as_savings || ["savings","money market","cd","hsa","cash management"].includes(a.subtype)));
  const creditAccs     = allAccounts.filter(a => a.type === "credit");
  const investmentAccs = allAccounts.filter(a => a.type === "investment");

  const totalCash     = checkingAccs.reduce((s,a) => s + parseFloat(a.balance||0), 0);
  const totalSavings  = savingsAccs.reduce((s,a) => s + parseFloat(a.balance||0), 0);
  const totalDebt     = creditAccs.reduce((s,a) => s + parseFloat(a.balance||0), 0);
  const totalInvest   = investmentAccs.reduce((s,a) => s + parseFloat(a.balance||0), 0);
  const netWorth      = totalCash + totalSavings + totalInvest - totalDebt;

  return (
    <div style={{ padding: "0 0 100px", fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <div style={{ padding: "20px 16px 10px", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <h2 style={{ color: COLORS.text, fontSize: 22, fontWeight: 800, margin: 0, letterSpacing: -0.5 }}>Accounts</h2>
        <button onClick={handleSync} disabled={syncing} style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "6px 12px", color: COLORS.textMuted, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
          {syncing ? "Syncing..." : "Sync"}
        </button>
      </div>

      <div style={{ padding: "0 12px", display: "flex", flexDirection: "column", gap: 10 }}>

        {/* Net Worth */}
        <div style={{ background: netWorth >= 0 ? COLORS.greenGlow : COLORS.redGlow, border: `1px solid ${netWorth >= 0 ? COLORS.green : COLORS.red}44`, borderRadius: 14, padding: "16px 18px" }}>
          <div style={{ color: COLORS.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6 }}>Net Worth</div>
          <div style={{ color: netWorth >= 0 ? COLORS.green : COLORS.red, fontSize: 32, fontWeight: 800, fontFamily: "monospace", letterSpacing: -1 }}>
            {netWorth < 0 ? "-" : ""}{fmt(Math.abs(netWorth))}
          </div>
          <div style={{ color: COLORS.textMuted, fontSize: 12, marginTop: 4 }}>Assets minus debt</div>
        </div>

        {/* Summary cards */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: "12px 14px" }}>
            <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", marginBottom: 5 }}>Cash</div>
            <div style={{ color: COLORS.green, fontSize: 16, fontWeight: 700, fontFamily: "monospace" }}>{fmt(totalCash)}</div>
          </div>
          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: "12px 14px" }}>
            <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", marginBottom: 5 }}>Savings</div>
            <div style={{ color: COLORS.green, fontSize: 16, fontWeight: 700, fontFamily: "monospace" }}>{fmt(totalSavings)}</div>
          </div>
          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: "12px 14px" }}>
            <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", marginBottom: 5 }}>Debt</div>
            <div style={{ color: COLORS.red, fontSize: 16, fontWeight: 700, fontFamily: "monospace" }}>-{fmt(totalDebt)}</div>
          </div>
          {investmentAccs.length > 0 && (
            <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: "12px 14px" }}>
              <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", marginBottom: 5 }}>Investments</div>
              <div style={{ color: COLORS.green, fontSize: 16, fontWeight: 700, fontFamily: "monospace" }}>{fmt(totalInvest)}</div>
            </div>
          )}
        </div>

        {/* Checking */}
        {checkingAccs.length > 0 && (
          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 18px" }}>
            <SectionHeader title="Checking" total={totalCash} totalColor={COLORS.green} COLORS={COLORS} />
            <div style={{ color: COLORS.textMuted, fontSize: 11, marginBottom: 10 }}>Spending accounts</div>
            {checkingAccs.map((a, i) => (
              <AccountCard key={i} account={a} COLORS={COLORS} />
            ))}
          </div>
        )}

        {/* Savings */}
        {savingsAccs.length > 0 && (
          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 18px" }}>
            <SectionHeader title="Savings" total={totalSavings} totalColor={COLORS.green} COLORS={COLORS} />
            <div style={{ color: COLORS.textMuted, fontSize: 11, marginBottom: 10 }}>Savings & money market</div>
            {savingsAccs.map((a, i) => (
              <AccountCard key={i} account={a} COLORS={COLORS} />
            ))}
          </div>
        )}

        {/* Credit Cards */}
        {creditAccs.length > 0 && (
          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 18px" }}>
            <SectionHeader title="Credit Cards" total={totalDebt} totalColor={COLORS.red} COLORS={COLORS} />
            <div style={{ color: COLORS.textMuted, fontSize: 11, marginBottom: 10 }}>Current balances</div>
            {creditAccs.map((a, i) => (
              <AccountCard key={i} account={a} COLORS={COLORS} />
            ))}
          </div>
        )}

        {/* Investments */}
        {investmentAccs.length > 0 && (
          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 18px" }}>
            <SectionHeader title="Investments" total={totalInvest} totalColor={COLORS.green} COLORS={COLORS} />
            <div style={{ color: COLORS.textMuted, fontSize: 11, marginBottom: 10 }}>Retirement & brokerage</div>
            {investmentAccs.map((a, i) => (
              <AccountCard key={i} account={a} COLORS={COLORS} />
            ))}
          </div>
        )}

      </div>
    </div>
  );
};

export default Accounts;
